import {
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { VoiceAgentRunModal } from '@/components/voice-agent/modals/VoiceAgentRunModal';
import { useGetTaskQuery } from '@/services/api';
import {
  useCancelPoliceOutreachMutation,
  useTerminateEnhancedBlandCallWorkflowMutation,
} from '@/services/api/blandService';
import {
  EnhancedBlandWorkflowStatus,
  EnhancedVoiceAIAgentRunSchema,
} from '@/types/enhanced-bland-call';

import { CallRow } from './CallRow';
import { StatusIndicator } from './StatusIndicator';

// Simple transcript modal component
const TranscriptModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  call: any;
}> = ({ isOpen, onClose, call }) => {
  if (!isOpen || !call) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Call Transcript
            </h2>
            <p className="text-sm text-gray-500">
              {call.phone_number} • {call.answered_by || 'Unknown'} •{' '}
              {call.duration_minutes
                ? `${call.duration_minutes.toFixed(1)}m`
                : 'Duration unknown'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {call.transcript ? (
            <div className="bg-gray-50 rounded-lg p-4 border">
              <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
                {call.transcript}
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-3">📝</div>
              <h3 className="font-medium text-gray-700 mb-1">
                No transcript available
              </h3>
              <p className="text-sm">
                This call may still be processing or didn&apos;t complete
                successfully.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface TaskRowProps {
  taskData: {
    task: {
      group_name: string;
      entity_name?: string | null;
      created_at?: string;
      task_id?: string;
    };
    runs: EnhancedVoiceAIAgentRunSchema[];
  };
  isExpanded: boolean;
  onToggle: () => void;
  onTaskSelect: (taskId: string) => void;
  matterId: string;
}

export const TaskRow: React.FC<TaskRowProps> = ({
  taskData,
  isExpanded,
  onToggle,
  onTaskSelect,
  matterId,
}) => {
  const { task, runs } = taskData;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<any>(null);

  // Fetch full task data for modal - only when modal is opened
  const { data: fullTask } = useGetTaskQuery(
    { taskId: task.task_id!, matterId },
    { skip: !isModalOpen || !task.task_id },
  );

  // Mutations for different types of cancellation
  const [terminateWorkflow, { isLoading: isTerminating }] =
    useTerminateEnhancedBlandCallWorkflowMutation();
  const [cancelOutreach, { isLoading: isCanceling }] =
    useCancelPoliceOutreachMutation();

  // Get most recent run status
  const mostRecentRun = [...runs].sort(
    (a, b) =>
      new Date(b.created_at || '').getTime() -
      new Date(a.created_at || '').getTime(),
  )[0];

  const runStatus = mostRecentRun?.status || 'unknown';

  // Define cancellable/terminable statuses using proper enum values
  const activeStatuses = [
    EnhancedBlandWorkflowStatus.STARTED,
    EnhancedBlandWorkflowStatus.THINKING,
    EnhancedBlandWorkflowStatus.CALL_IN_PROGRESS,
    EnhancedBlandWorkflowStatus.WAITING,
  ];

  // Check ONLY the most recent run's status
  const isActive =
    mostRecentRun &&
    activeStatuses.includes(
      mostRecentRun.status as EnhancedBlandWorkflowStatus,
    );
  const isScheduled =
    mostRecentRun &&
    mostRecentRun.status === EnhancedBlandWorkflowStatus.SCHEDULED;

  // Show X button only for scheduled runs and active (running) runs
  const canCancelOrTerminate = isScheduled || isActive;

  // Find the scheduled run for cancellation (only if most recent run is scheduled)
  const scheduledRun = isScheduled ? mostRecentRun : null;

  // Handle terminate/cancel based on run status
  const handleTerminate = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!task.task_id) return;

    try {
      if (isScheduled && scheduledRun) {
        // Use cancel endpoint for scheduled runs
        const action = 'Cancel';
        if (
          !window.confirm(
            `${action} this scheduled voice agent run? This cannot be undone.`,
          )
        ) {
          return;
        }

        await terminateWorkflow({ matterId, taskId: task.task_id }).unwrap();
        toast.success('Scheduled run cancelled successfully');
      } else {
        // Use terminate endpoint for active runs
        const action = 'Terminate';
        if (
          !window.confirm(
            `${action} this active voice agent run? This cannot be undone.`,
          )
        ) {
          return;
        }

        await terminateWorkflow({ matterId, taskId: task.task_id }).unwrap();
        toast.success('Active run terminated successfully');
      }
    } catch (error: any) {
      const action = isScheduled ? 'cancel' : 'terminate';
      toast.error(error?.data?.error || `Failed to ${action} run`);
    }
  };

  const handleViewTranscript = (callId: string) => {
    // Find the call across all runs
    const foundCall = runs
      .flatMap((run) => run.call_results || [])
      .find((call: any) => call.id === callId);

    if (foundCall) {
      setSelectedCall(foundCall);
      setTranscriptModalOpen(true);
    }
  };

  // Convert most recent run to workflow state for modal
  const workflowState = mostRecentRun
    ? {
        workflow_id: mostRecentRun.workflow_id || mostRecentRun.id,
        task_id: task.task_id!,
        status: mostRecentRun.status as any, // Cast to EnhancedBlandWorkflowStatus
        call_results: mostRecentRun.call_results || [],
        calls_made: mostRecentRun.call_results?.length || 0,
        max_calls_allowed: mostRecentRun.max_calls_allowed || 3,
        last_message: mostRecentRun.summary || '',
        event_log: mostRecentRun.event_log || [],
        error_message: mostRecentRun.error_message || null,
        created_at: mostRecentRun.created_at || '',
        updated_at: mostRecentRun.updated_at || mostRecentRun.created_at || '',
        readiness: {
          missing_items: [],
          summary: 'Ready',
          is_ready: true,
        },
      }
    : null;

  const isLoading = isTerminating || isCanceling;

  return (
    <>
      <div className="border-b border-gray-100 last:border-b-0">
        <div
          className="flex items-center py-2 px-4 cursor-pointer hover:bg-gray-50 text-xs"
          onClick={onToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
        >
          {/* Expand/collapse icon */}
          {isExpanded ? (
            <ChevronDownIcon className="h-3 w-3 text-gray-400 mr-2 flex-shrink-0" />
          ) : (
            <ChevronRightIcon className="h-3 w-3 text-gray-400 mr-2 flex-shrink-0" />
          )}

          <div className="flex-1 flex items-center justify-between min-w-0">
            {/* Task info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 truncate">
                  {task.group_name}
                </span>
                {task.entity_name && (
                  <span className="text-gray-500 truncate">
                    → {task.entity_name}
                  </span>
                )}
                {isActive && (
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0" />
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <StatusIndicator
                status={runStatus}
                isScheduled={
                  runStatus === EnhancedBlandWorkflowStatus.SCHEDULED
                }
                size="sm"
              />

              {/* Cancel/Terminate button - only for scheduled or active runs */}
              {canCancelOrTerminate && (
                <button
                  type="button"
                  onClick={handleTerminate}
                  disabled={isLoading}
                  className="text-red-600 hover:text-red-800 px-1 py-0.5 rounded hover:bg-red-100 disabled:opacity-50"
                  title={
                    isScheduled
                      ? 'Cancel scheduled run'
                      : 'Terminate active run'
                  }
                >
                  {isLoading ? (
                    <div className="w-3 h-3 border border-red-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <XMarkIcon className="h-3 w-3" />
                  )}
                </button>
              )}

              {/* Modal button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsModalOpen(true);
                }}
                className="text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-100 text-xs font-medium"
                title="View voice agent runs"
              >
                Details
              </button>

              {/* Task detail button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (task.task_id) {
                    onTaskSelect(task.task_id);
                  }
                }}
                className="text-gray-600 hover:text-gray-800 px-1 py-0.5 rounded hover:bg-gray-100"
                title="View task details"
              >
                →
              </button>
            </div>
          </div>
        </div>

        {/* Expanded content - simplified */}
        {isExpanded && (
          <div className="bg-gray-50 border-t border-gray-100">
            {runs.map((run, runIndex) => (
              <div
                key={run.id}
                className="border-b border-gray-200 last:border-b-0"
              >
                {/* Run header - simplified */}
                <div className="px-6 py-1 bg-gray-100 text-xs text-gray-600 font-mono flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span>Run {runIndex + 1}</span>
                    <StatusIndicator
                      status={run.status || 'unknown'}
                      isScheduled={
                        run.status === EnhancedBlandWorkflowStatus.SCHEDULED
                      }
                      size="sm"
                    />
                  </div>
                  <span>{run.call_results?.length || 0} calls</span>
                </div>

                {/* Calls for this run */}
                <div className="divide-y divide-gray-200">
                  {run.call_results && run.call_results.length > 0 ? (
                    run.call_results.map((call: any) => (
                      <CallRow
                        key={call.id}
                        call={call}
                        runStatus={run.status}
                        workflowId={run.workflow_id}
                        showRunIndicator={false}
                        onViewTranscript={handleViewTranscript}
                        onRouted={() => {
                          // Optionally refresh the data
                          window.location.reload();
                        }}
                        className="pl-8"
                      />
                    ))
                  ) : (
                    <div className="px-8 py-2 text-xs text-gray-500 italic">
                      No calls yet
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Voice Agent Run Modal */}
      {isModalOpen && fullTask && (
        <VoiceAgentRunModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          task={fullTask}
          matterId={matterId}
          workflow={workflowState}
          onRefetch={() => {
            // Could add refetch logic here if needed
          }}
        />
      )}

      {/* Transcript Modal */}
      <TranscriptModal
        isOpen={transcriptModalOpen}
        onClose={() => {
          setTranscriptModalOpen(false);
          setSelectedCall(null);
        }}
        call={selectedCall}
      />
    </>
  );
};
